<template>
  <v-container class="contract-container pa-0">
    <!-- 캡쳐시 화면 전체 덮는 레이어 -->
    <div v-if="captureMode" class="cover"></div>

    <!-- 접이식 동의 항목 -->
    <div ref="capture">
      <h3 class="text-center py-4 mt-8">여신거래계약서</h3>

      <div class="contract-notice mb-9">
        본인은 ㈜펀다에이아이와 아래의 조건에 따라 여신거래를 함에 있어 아래 약관이 적용됨을 승인하고, 다음
        각 조항을 확약합니다.
      </div>

      <ContractExpansionSection
        v-for="(section, index) in sections"
        :key="index"
        :loan="loan"
        :isComplete.sync="section.isComplete"
        :isOpen.sync="section.isOpen"
        :isFocus="section.isFocus"
        :title="section.title"
        :content="section.component"
        :ref="section.refId"
        @complete="navigateNextStep"
        @sign="formSource.signature = $event"
      />
    </div>

    <!-- 하단 영역 -->
    <v-footer fixed class="pa-0 ma-0">
      <v-layout>
        <v-btn
          block
          depressed
          tile
          color="primary"
          height="52"
          :disabled="isSubmitLoading || showErrorMode"
          :loading="isSubmitLoading"
          @click="submit"
        >
          <b class="font-size--18 font-weight-black white--text">다음</b>
        </v-btn>
      </v-layout>
    </v-footer>
  </v-container>
</template>

<script>
import html2canvas from 'html2canvas'
import { mapActions, mapState, mapMutations } from 'vuex'
import { sleep } from '@/util'

export default {
  components: {
    ContractExpansionSection: () =>
      import('@/components/loan/contract/ContractExpansionSection.vue'),
  },
  data() {
    return {
      sections: [
        {
          isOpen: false,
          isComplete: true,
          title: '채권자 정보',
          component: () => import('@/components/loan/contract/content/CreditorInfo.vue'),
        },
        {
          isOpen: true,
          isComplete: false,
          title: '대출자 정보',
          errorMessage: '대출자 정보 확인 버튼을 클릭해주세요.',
          refId: 'DebtorInfo',
          component: () => import('@/components/loan/contract/content/DebtorInfo.vue'),
        },
        {
          isOpen: false,
          isComplete: false,
          title: '대출 정보',
          errorMessage: '만기일, 한도 금액, 이율을 작성 및 대출 정보 확인 버튼을 클릭해주세요.',
          refId: 'LoanInfo',
          component: () => import('@/components/loan/contract/content/LoanInfo.vue'),
        },
        {
          isOpen: false,
          isComplete: false,
          title: '개인(신용)정보 조회 동의서',
          errorMessage: '개인(신용)정보 조회 동의서에 동의 버튼을 클릭해주세요.',
          refId: 'CreditTerm',
          component: () => import('@/components/loan/contract/content/CreditTerm.vue'),
        },
        {
          isOpen: false,
          isComplete: false,
          title: '여신한도거래 약관 동의',
          errorMessage: '여신한도거래 약관 확인 버튼 클릭 및 서명해주세요.',
          refId: 'AgreeSign',
          component: () => import('@/components/loan/contract/content/AgreeSign.vue'),
        },
      ],

      loan: null,
      formSource: {
        signature: null,
        image: null,
        contract: null,
        term: null,
      },

      isSubmitLoading: false,

      testViewUrl: null,
    }
  },
  computed: {
    ...mapState('ui', ['captureMode', 'showErrorMode']),
    contractAgreementForm() {
      return {
        offer: this.loan?.offer?.uuid,
        contract: this.formSource.contract?.uuid,
        term: this.formSource.term?.uuid,
        isAgreed: true,
        signature: this.formSource.signature, // 사인
        image: this.formSource.image, // 계약서 전체
      }
    },
  },
  async created() {
    this.loan = await this.getLoansDetail(this.$route.params.uuid)

    // 기본계약서
    this.formSource.contract = await this.getContractCurrent()

    // 여신한도거래계약서
    this.formSource.term = await this.getContractTermsCode({ code: 'LIMIT_TRANSACTION' })
  },
  methods: {
    ...mapActions('loan', ['getLoansDetail', 'executeLoan', 'getLoansByOngoing']),
    ...mapMutations('ui', ['setCaptureMode']),
    ...mapActions('ui', ['activateErrorMode']),
    ...mapActions('agreement', [
      'getContractCurrent',
      'getContractTermsCode',
      'addContractAgreements',
    ]),

    /**
     * @return 미완료 단계
     */
    getIncompleteStep() {
      return this.sections.find((e) => !e.isComplete)
    },

    /**
     * 미완료 단계 찾아서 스크롤 이동
     */
    async navigateNextStep() {
      const incompleteStep = this.getIncompleteStep()
      if (!incompleteStep) return

      for (const section of this.sections) {
        section.isFocus = false
      }
      incompleteStep.isOpen = true
      incompleteStep.isFocus = true

      // 최초 진입시 해당 영역 상단으로 스크롤 이동
      if (!this.showErrorMode) {
        await setTimeout((_) => {
          this.$refs?.[incompleteStep.refId]?.[0]?.$el?.scrollIntoView(true, { behavior: 'smooth' })
        }, 100)
      }
    },

    /**
     * 모든 단계가 완료되었는지 검사
     * @return 검사 통과 여부
     */
    async validate() {
      const incompleteStep = this.getIncompleteStep()
      if (!incompleteStep) return true

      // 미완료 단계 포커스 및 붉은색 표시 활성화
      await this.$swal.basic.alert(incompleteStep.errorMessage)
      await this.navigateNextStep()
      this.activateErrorMode()

      return false
    },

    /**
     * 화면 캡쳐
     */
    async capture() {
      for (const section of this.sections) {
        section.isOpen = true
      }

      await sleep(1500)

      const element = this.$refs.capture
      const canvas = await html2canvas(element, {
        x: 0,
        y: 0,
        scale: 1,
      })

      const blob = await new Promise((resolve) => canvas.toBlob(resolve))
      this.formSource.image = blob
      this.testViewUrl = window.URL.createObjectURL(blob)
    },

    /**
     * 계약서 작성 완료
     */
    async submit() {
      if (!(await this.validate()) || this.isSubmitLoading) return

      try {
        this.isSubmitLoading = true
        this.setCaptureMode(true)
        await this.capture()

        // 계약서 저장
        await this.addContractAgreements(this.contractAgreementForm)
        // 대출 실행
        await this.executeLoan(this.loan.uuid)
        // 대출 상태 업데이트
        await this.getLoansByOngoing()

        // 계약서 작성 완료 이벤트 발생
        this.$emit('contract-complete', this.loan)
      } finally {
        this.setCaptureMode(false)
        this.isSubmitLoading = false
      }

      // 테스트 확인용
      // this.$swal.basic.alert({
      //   html: `<div style='height: 500px; overflow:scroll'><p>test view</p><img src='${this.testViewUrl}' /></div>`,
      // })
    },
  },
}
</script>

<style lang="scss" scoped>
.contract-container {
  position: relative;

  .contract-notice {
    background-color: #f4faff;
    padding: 20px;
    font-size: 16px;
    line-height: 1.5;
    color: #333333;
  }
  .cover {
    background-color: transparent;
    position: fixed;
    width: 100%;
    height: 120%;
    top: 0;
    z-index: 3;
    touch-action: none;
  }
}
</style>
